import React, { useState, useCallback } from 'react';
import { kebabCase } from 'lodash';
import PropTypes from 'prop-types';
import Checkbox from 'react-three-state-checkbox';
import { Modal, ModalBody, ModalHeader, ModalFooter, Button } from 'reactstrap';
import * as ramda from 'ramda';
import entityComposition from '../common/classes/entityComposition';
const createWarning = query => {
  const extra = 'Employees associated with the selection you made must be present in all the jobs associated with your selection.  Try filtering by job before assigning.';
  if (!query || !query.filters || !query.filters.job) {
    return `No employees associated with the selection you made.  ${extra}`;
  }
  const jobs = ramda.uniq(query.filters.job.map(job => job.jobNumber));
  const last = jobs.pop();
  return jobs.length === 0 ? `No employees found for your selection with job number ${last}.  ${extra}` : `No employees found that are assigned to jobs with numbers ${jobs.join(', ')} and ${last}.  ${extra}`;
};
const allSelectedForEmployee = (employeeID, assignments, selectedIDs, entity) => {
  const removeNonEmployeeRecords = list => list.filter(item => item.userID === employeeID);
  const extractAssignedIDs = list => list.map(a => a[`${entity}ID`]);
  const sortIDs = ramda.sort((l, x) => l.localeCompare(x));
  const assignmentIDs = ramda.pipe(removeNonEmployeeRecords, extractAssignedIDs, ramda.uniq, sortIDs)(assignments);
  const sortedIDs = sortIDs(selectedIDs);
  return ramda.equals(assignmentIDs, sortedIDs);
};
const AssignmentModal = ({
  employeeList: _employeeList = [],
  employeesSelected: _employeesSelected = [],
  entity: _entity = 'report',
  assignments,
  isOpen,
  closeModal,
  employeeModalAssignIndexQuery,
  employeeModalAssignActions: {
    indexCancel,
    select,
    selectClear,
    selectClearAll
  },
  selected,
  saveAssignments
}) => {
  const [removals, setRemovals] = useState([]);

  // functions to call when the modal closes
  const cleanUpActions = useCallback(() => {
    setRemovals([]); // clear removal state
    selectClearAll(); // clear selections
    indexCancel(); // clear employee list
  }, [indexCancel, selectClearAll, setRemovals]);
  const handleSelect = useCallback(({
    id
  }, isSelected) => {
    const filteredRemovals = removals.filter(removeID => removeID !== id);
    // The following logic helps us determine whether we've reset to the original state or not
    // const assignments = selected.map(selection => extractAssignedFromContent(selection.content));
    const selectDeselect = assignments.length !== 0 && !assignments.some(({
      userID
    }) => userID === id);
    const allSelected = allSelectedForEmployee(id, assignments, selected, _entity);
    // If a selection was made _and_ the selection wasn't made in the original list
    if (isSelected && !allSelected) {
      select(id);
      // A selection wasn't made and the checkbox wasn't selected and then deselected before saving
    } else if (!isSelected && !selectDeselect) {
      selectClear(id);
      filteredRemovals.push(id);
    } else if (!isSelected) {
      selectClear(id);
    }
    setRemovals(filteredRemovals);
  }, [assignments, _entity, removals, select, selectClear, selected]);
  const handleClose = useCallback(() => {
    cleanUpActions();
    closeModal();
  }, [cleanUpActions, closeModal]);
  const handleSave = useCallback(() => {
    saveAssignments(_employeesSelected, removals);
    cleanUpActions();
  }, [_employeesSelected, removals, saveAssignments, cleanUpActions]);
  const noEmployeesMessage = createWarning(employeeModalAssignIndexQuery);
  return /*#__PURE__*/React.createElement(Modal, {
    isOpen: isOpen
  }, /*#__PURE__*/React.createElement(ModalHeader, null, _employeeList.length > 0 ? 'Select employees to assign' : 'No employees available'), /*#__PURE__*/React.createElement(ModalBody, null, _employeeList.length > 0 ? _employeeList.map(employee => {
    const {
      id,
      firstName,
      lastName
    } = employee;
    const displayName = `${firstName} ${lastName}`;
    const onSelect = event => handleSelect(employee, event.target.checked);
    const allReports = allSelectedForEmployee(id, assignments, selected, _entity);
    const atLeastOne = assignments.some(({
      userID
    }) => userID === id);
    const listSelected = _employeesSelected.some(employeeID => employeeID === id);
    const removalSelected = removals.some(removalID => removalID === id);
    const checked = (allReports || listSelected) && !removalSelected;
    const indeterminate = !allReports && atLeastOne && !listSelected && !removalSelected;
    return /*#__PURE__*/React.createElement("div", {
      key: id
    }, /*#__PURE__*/React.createElement(Checkbox, {
      className: `t-am-${kebabCase(displayName)} three-state-checkbox`,
      type: "checkbox",
      onChange: onSelect,
      checked: checked,
      indeterminate: indeterminate
    }), displayName);
  }) : noEmployeesMessage), /*#__PURE__*/React.createElement(ModalFooter, null, /*#__PURE__*/React.createElement(Button, {
    onClick: handleClose,
    className: "t-am-cancel"
  }, "Cancel"), /*#__PURE__*/React.createElement(Button, {
    disabled: !_employeesSelected.length && !removals.length,
    className: "t-am-assign",
    onClick: handleSave
  }, "Assign")));
};
AssignmentModal.propTypes = {
  assignments: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  closeModal: PropTypes.func.isRequired,
  saveAssignments: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  employeeList: PropTypes.arrayOf(PropTypes.shape({})),
  employeesSelected: PropTypes.arrayOf(PropTypes.string),
  employeeModalAssignIndexQuery: PropTypes.shape({}).isRequired,
  selected: PropTypes.arrayOf(PropTypes.string).isRequired,
  employeeModalAssignActions: PropTypes.shape({
    select: PropTypes.func,
    selectClear: PropTypes.func,
    selectClearAll: PropTypes.func,
    serverFilter: PropTypes.func,
    indexCancel: PropTypes.func
  }).isRequired,
  entity: PropTypes.string
};
export { AssignmentModal as TestAssignmentModal };
export default entityComposition(AssignmentModal, [], {
  employeeList: state => state.employeeModalAssign.list,
  employeesSelected: state => state.employeeModalAssign.selected,
  employeeModalAssignIndexQuery: state => state.employeeModalAssign.indexQuery,
  loading: state => state.employeeModalAssign.loading
}, ['employeeModalAssign']);